import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import useAppState, { matchDarwinTimestamp, type NTJRace } from './useAppState';
import { type MeetingsDatedQuery, RaceStatus } from '../generated/graphql';
import dayjs from 'dayjs';
import useMeetingsDated from './useMeetingsDated';

type RawNextRace = {
  race: MeetingsDatedQuery['meetingsDated'][number]['races'][number];
  meeting: MeetingsDatedQuery['meetingsDated'][number];
};

export default function useNextToJump(
  controllerId: string,
  channelNumber: number,
  skip = false
) {
  const { currentTime } = useAppState();

  const [rawNextRaces, setRawNextRaces] = useState([] as RawNextRace[]);
  const [trackToUse, setTrackToUse] = useState<string>('');
  const [nextToJump, setNextToJump] = useState<NTJRace[]>([]);

  const { today, tomorrow } = useMemo(() => {
    const dt = matchDarwinTimestamp(currentTime);

    return {
      today: dt,
      tomorrow: dt.add(2, 'day'),
    };
  }, [currentTime]);

  const meetings = useMeetingsDated(
    today.toISOString(),
    tomorrow.toISOString()
  );

  useEffect(() => {
    if (skip || !controllerId || !channelNumber) return;

    const c = new AbortController();
    const getTrack = async () => {

      const data = await axios.post(
        `${process.env.REACT_APP_LFI_API}/srv.lfi.LFIService/GetChannelAllocation`,
        {
          // "channelNumber": Number(params.screenNumber),
          controllerId: controllerId,
          meetingDate: matchDarwinTimestamp().format('YYYY-MM-DD HH:mm:ss'),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Key ${process.env.REACT_APP_LFI_KEY}`,
          },
        }
      );

      const screenToUse = data.data?.allocations?.find(
        (a: { channelNumber: number }) =>
          a.channelNumber === Number(channelNumber)
      );

      if (screenToUse?.selectedMeeting)
        setTrackToUse(screenToUse.selectedMeeting);
    };

    getTrack();

    return () => c.abort();
  }, [channelNumber, controllerId, skip]);

  const { meetingToUse, meetingsToUse } = useMemo(() => {
    const splitTracksToUse = trackToUse.split(',');

    const ms =
      splitTracksToUse.length > 1 && !!meetings.length
        ? meetings.filter((m) => splitTracksToUse.includes(m.track.name))
        : null;

    const m =
      meetings.length && trackToUse !== ''
        ? meetings.find((m) => m.track.name === trackToUse)
        : null;

    return {
      meetingsToUse: ms,
      meetingToUse: m,
    };
  }, [meetings, trackToUse]);

  const raceSet = useMemo(() => {

    return meetingToUse
      ? meetingToUse.races.map((r) => ({
          race: r,
          meeting: meetingToUse,
        }))
      : meetingsToUse
      ? meetingsToUse.flatMap((m) =>
          m.races.map((r) => ({
            race: r,
            meeting: m,
          }))
        )
      : null;
  }, [meetingToUse, meetingsToUse]);

  const getN2j = useCallback(() => {
    if (skip || !raceSet) return;

    const allRacesFinal =
      raceSet.filter(({ race: r }) => r.status !== RaceStatus.Final).length ===
      0;

    const nextRaces = raceSet
      .filter(({ race: r }) =>
        allRacesFinal
          ? r.status === RaceStatus.Final
          : meetingToUse // if single - show resulting - otherwise show open only
          ? r.status !== RaceStatus.Final && r.status !== RaceStatus.Abandoned
          : r.status === RaceStatus.Open
      )
      .sort(
        ({ race: a }, { race: b }) =>
          dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf()
      )
      .slice(0, 12);

    setRawNextRaces(nextRaces);

    setNextToJump(
      nextRaces.map(({ race: r, meeting }) => {
        return {
          ...r,
          meeting: {
            id: meeting.id,
            type: meeting.type,
            track: meeting.track,
          },
        };
      })
    );
  }, [meetingToUse, raceSet, skip]);

  useEffect(() => {
    if (!meetingToUse && !meetingsToUse) return;
    getN2j();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingsToUse, meetingToUse, getN2j]);

  return useMemo(() => {
    return {
      meeting: rawNextRaces[0]?.meeting,
      nextToJump,
      getN2j,
      isMultipleMeetings: !meetingToUse,
    };
  }, [getN2j, meetingToUse, nextToJump, rawNextRaces]);
}
